import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AbstractEntityService } from '@app/shared/abstract/abstract-entity.service';
import { ResourceData } from '@app/shared/abstract/resource.data';
import { EventTemplateModel } from './event-template.model';
import { EventModel } from '@app/events/shared/event.model';

export interface TemplateApplicationResult {
  message: string;
  data: {
    template: EventTemplateModel;
    created_items: Array<any>;
    event: EventModel;
  };
}

export interface TemplateFromEventData {
  template_name: string;
  template_description?: string;
  event_type: EventTemplateModel['event_type'];
  timeline_item_ids: number[];
}

export interface TemplateFromEventResult {
  message: string;
  data: {
    template: EventTemplateModel;
    source_event: {
      id: number;
      name: string;
      reference: string;
    };
    timeline_items_count: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class EventTemplateService extends AbstractEntityService<EventTemplateModel> {
  constructor(http: HttpClient) {
    super('/api/event-templates', http);
  }

  /**
   * Apply a template to an event
   */
  applyToEvent(templateId: number, eventId: number): Observable<TemplateApplicationResult> {
    return this.http.post<TemplateApplicationResult>(
      `${this.apiUrl}${this.basePath}/${templateId}/apply/${eventId}`,
      {}
    );
  }

  /**
   * Create a template from existing event timeline items
   */
  createFromEvent(eventId: number, data: TemplateFromEventData): Observable<TemplateFromEventResult> {
    return this.http.post<TemplateFromEventResult>(
      `${this.apiUrl}/api/events/${eventId}/create-template`,
      data
    );
  }

  /**
   * Get available event types for templates
   */
  getEventTypes(): EventTemplateModel['event_type'][] {
    return [
      'Wedding',
      'Corporate Event',
      'Christmas Party',
      'Private Event',
      'Tasting Event',
      'Open Day',
      'Sales Day'
    ];
  }

  /**
   * Save template with items
   */
  saveWithItems(template: EventTemplateModel): Observable<ResourceData<EventTemplateModel>> {
    const payload = {
      name: template.name,
      event_type: template.event_type,
      venue_id: template.venue_id,
      description: template.description,
      items: template.items?.map(item => ({
        id: item.id,
        name: item.name,
        description: item.description,
        days_offset: item.days_offset,
        time_of_day: item.time_of_day,
        offset_amount: item.offset_amount,
        offset_unit: item.offset_unit,
        offset_direction: item.offset_direction
      }))
    };

    if (template.id) {
      return this.put(template.id, JSON.stringify(payload));
    } else {
      return this.post('', JSON.stringify(payload));
    }
  }

  /**
   * Delete a template
   */
  deleteTemplate(id: number): Observable<any> {
    return this.delete(id.toString());
  }
}